import { useState, useEffect, useCallback } from 'react';
import { DiagnosticFormData } from '../../../types/diagnostic';
import { getCitiesFromPostalCode, CommuneData } from '../../../services/addressService';
import { getCrimeStatistics, getCrimeComparison } from '../../../services/criminalityService';
import { motion, AnimatePresence } from 'framer-motion';
import { dpsTheme } from '../../../styles/dpsTheme';

interface LocationProps {
  formData: DiagnosticFormData;
  updateFormData: (data: Partial<DiagnosticFormData>) => void;
}

export default function Location({ formData, updateFormData }: LocationProps) {
  const [isLoadingCity, setIsLoadingCity] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastSearchedPostalCode, setLastSearchedPostalCode] = useState('');
  const [availableCities, setAvailableCities] = useState<CommuneData[]>([]);
  const [showCityDropdown, setShowCityDropdown] = useState(false);

  const lookupCities = useCallback(async (postalCode: string) => {
    if (postalCode === lastSearchedPostalCode || postalCode.length !== 5) return;
    
    setIsLoadingCity(true);
    setError(null);
    setAvailableCities([]);
    
    try {
      const cities = await getCitiesFromPostalCode(postalCode);
      if (cities.length > 0) {
        setAvailableCities(cities);
        setLastSearchedPostalCode(postalCode);
        
        if (cities.length === 1) {
          const city = cities[0];
          updateFormData({
            city: city.nom,
            codeInsee: city.code,
            codeDepartement: city.codeDepartement,
            cityData: {
              population: city.population,
              logements: city.logements
            }
          });
          setShowCityDropdown(false);

          // Await crime stats so they are in formData before the spinner disappears
          try {
            const crimeData = await getCrimeStatistics(city.code, city.nom);
            if (crimeData) {
              const comparison = await getCrimeComparison(crimeData, city.codeDepartement);
              updateFormData({ crimeData, crimeComparison: comparison });
            }
          } catch (crimeErr) {
            console.error('Error fetching crime statistics:', crimeErr);
          }
        } else {
          // Si plusieurs villes, on affiche la liste déroulante
          setShowCityDropdown(true);
          updateFormData({ 
            city: '',
            cityData: undefined
          });
        }
      } else {
        setError('Code postal non trouvé');
      }
    } catch (err) {
      setError('Erreur lors de la recherche des communes');
    } finally {
      setIsLoadingCity(false);
    }
  }, [lastSearchedPostalCode, updateFormData]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.postalCode.length === 5) {
        lookupCities(formData.postalCode);
      } else if (formData.city && !formData.postalCode) {
        updateFormData({ 
          city: '',
          cityData: undefined
        });
        setAvailableCities([]);
        setShowCityDropdown(false);
      }
    }, 1000);

    return () => clearTimeout(timeoutId);
  }, [formData.postalCode, lookupCities, formData.city, updateFormData]);

  const handlePostalCodeChange = (value: string) => {
    const numericValue = value.replace(/[^0-9]/g, '');
    if (numericValue.length <= 5) {
      if (numericValue !== formData.postalCode) {
        updateFormData({ 
          postalCode: numericValue,
          city: '',
          cityData: undefined
        });
        if (numericValue.length < 5) {
          setError(null);
          setLastSearchedPostalCode('');
          setAvailableCities([]);
          setShowCityDropdown(false);
        }
      }
    }
  };

  const handleCitySelect = async (city: CommuneData) => {
    updateFormData({
      city: city.nom,
      codeInsee: city.code,
      codeDepartement: city.codeDepartement,
      cityData: {
        population: city.population,
        logements: city.logements
      }
    });
    setShowCityDropdown(false);

    try {
      const crimeData = await getCrimeStatistics(city.code, city.nom);
      if (crimeData) {
        const comparison = await getCrimeComparison(crimeData, city.codeDepartement);
        updateFormData({
          crimeData,
          crimeComparison: comparison
        });
      }
    } catch (error) {
      console.error('Error fetching crime statistics:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <h2 style={{
        fontSize: dpsTheme.typography.fontSize['3xl'],
        fontWeight: dpsTheme.typography.fontWeight.bold,
        color: dpsTheme.colors.text.primary,
        marginBottom: dpsTheme.spacing['2xl']
      }}>
        Localisation
      </h2>

      {/* Privacy reassurance notice */}
      <div style={{
        background: 'rgba(59, 130, 246, 0.1)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        borderRadius: '12px',
        padding: '16px',
        marginBottom: '24px',
        display: 'flex',
        gap: '12px'
      }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2" style={{ flexShrink: 0, marginTop: '2px' }}>
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        <div>
          <p style={{ fontSize: '14px', fontWeight: 600, color: '#1e40af', marginBottom: '4px' }}>
            Protection de votre vie privée
          </p>
          <p style={{ fontSize: '13px', color: '#3b82f6', lineHeight: '1.5' }}>
            Nous ne demandons pas votre adresse complète. Seul le code postal est nécessaire pour les statistiques de cambriolages locales.
          </p>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: dpsTheme.spacing.xl }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: dpsTheme.spacing.lg }}>
          <div>
            <label htmlFor="postalCode" style={{
              display: 'block',
              fontSize: dpsTheme.typography.fontSize.md,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.secondary,
              marginBottom: dpsTheme.spacing.sm
            }}>
              Code postal *
            </label>
            <input
              type="text"
              id="postalCode"
              value={formData.postalCode}
              onChange={(e) => handlePostalCodeChange(e.target.value)}
              style={{
                width: '100%',
                padding: dpsTheme.spacing.md,
                borderRadius: dpsTheme.borderRadius.sm,
                border: `1px solid ${error ? dpsTheme.colors.priority.high.border : dpsTheme.colors.border.primary}`,
                background: dpsTheme.colors.background.darker,
                color: dpsTheme.colors.text.primary,
                fontSize: dpsTheme.typography.fontSize.base
              }}
              placeholder="Ex: 75001"
              maxLength={5}
            />
            {error && (
              <p style={{
                marginTop: dpsTheme.spacing.sm,
                fontSize: dpsTheme.typography.fontSize.sm,
                color: dpsTheme.colors.priority.high.text
              }}>
                {error}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="city" style={{
              display: 'block',
              fontSize: dpsTheme.typography.fontSize.md,
              fontWeight: dpsTheme.typography.fontWeight.semibold,
              color: dpsTheme.colors.text.secondary,
              marginBottom: dpsTheme.spacing.sm
            }}>
              Commune *
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                id="city"
                value={formData.city}
                style={{
                  width: '100%',
                  padding: dpsTheme.spacing.md,
                  borderRadius: dpsTheme.borderRadius.sm,
                  border: `1px solid ${dpsTheme.colors.border.primary}`,
                  background: isLoadingCity ? dpsTheme.colors.background.card : dpsTheme.colors.background.darker,
                  color: dpsTheme.colors.text.primary,
                  fontSize: dpsTheme.typography.fontSize.base
                }}
                placeholder="Sélectionnez votre commune"
                readOnly
              />
              {isLoadingCity && (
                <div style={{
                  position: 'absolute',
                  right: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)'
                }}>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: `2px solid ${dpsTheme.colors.border.primary}`,
                    borderTop: `2px solid ${dpsTheme.colors.accent.blue.primary}`,
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                </div>
              )}

              <AnimatePresence>
                {showCityDropdown && availableCities.length > 1 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    style={{
                      position: 'absolute',
                      zIndex: 10,
                      width: '100%',
                      marginTop: dpsTheme.spacing.sm,
                      background: dpsTheme.colors.background.card,
                      backdropFilter: 'blur(20px)',
                      borderRadius: dpsTheme.borderRadius.sm,
                      border: `1px solid ${dpsTheme.colors.border.primary}`,
                      boxShadow: dpsTheme.shadows.hover
                    }}
                  >
                    <ul style={{ maxHeight: '240px', overflowY: 'auto', padding: dpsTheme.spacing.sm }}>
                      {availableCities.map((city) => (
                        <li
                          key={city.code}
                          onClick={() => handleCitySelect(city)}
                          style={{
                            padding: dpsTheme.spacing.md,
                            borderRadius: dpsTheme.borderRadius.sm,
                            cursor: 'pointer',
                            fontSize: dpsTheme.typography.fontSize.base,
                            color: dpsTheme.colors.text.primary,
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = dpsTheme.colors.background.darker;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'transparent';
                          }}
                        >
                          {city.nom}
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            {availableCities.length > 1 && !formData.city && (
              <p style={{
                marginTop: dpsTheme.spacing.sm,
                fontSize: dpsTheme.typography.fontSize.sm,
                color: dpsTheme.colors.text.muted
              }}>
                Plusieurs communes trouvées, veuillez en sélectionner une
              </p>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
}